import { useState, useMemo } from 'react';
import { Pause, Play, Pencil, Trash2, CreditCard, Landmark, X, Search, Link2, Repeat, SearchX } from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import {
  useRecurringSales,
  useUpdateRecurringSale,
  useCancelRecurringSale,
  RECURRING_FREQUENCIES,
  RECURRING_FREQUENCY_LABELS,
  type RecurringSale,
  type RecurringFrequency,
} from '@/hooks/use-recurring-sales';
import {
  useRPCustomers,
  useRPPaymentLinks,
  useUpdateRPPaymentLink,
  useUpdateRPPaymentLinkStatus,
  type RPCustomer,
  type RPPaymentLink,
} from '@/hooks/use-receive-payments';
import { useLocationStore } from '@/stores/location-store';
import { formatCurrency, formatDateTime, cn } from '@/lib/utils';
import { toast } from '@/stores/toast-store';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DateInput } from '@/components/ui/date-input';
import { StatusBadge } from '@/components/shared/status-badge';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { usePermissions } from '@/hooks/use-permissions';

const inputBase = 'h-10 w-full rounded-lg border border-input bg-white px-3 text-sm outline-none focus:ring-1';
const inputCls = `${inputBase} focus:border-primary focus:ring-primary`;
const PER_PAGE_OPTIONS = [10, 25, 50, 100];

type RecurringRow =
  | { source: 'sale'; id: string; customerName: string; sale: RecurringSale }
  | { source: 'link'; id: string; customerName: string; link: RPPaymentLink };

export function RecurringTab() {
  const { selectedLocationId } = useLocationStore();
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'paused' | 'cancelled' | 'scheduled'>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const { data, isLoading } = useRecurringSales({
    locationId: selectedLocationId || undefined,
    status: statusFilter || undefined,
  });
  const { data: linksData, isLoading: linksLoading } = useRPPaymentLinks({
    locationId: selectedLocationId || undefined,
  });
  const updateRule = useUpdateRecurringSale();
  const cancelRule = useCancelRecurringSale();
  const updateLinkStatus = useUpdateRPPaymentLinkStatus();
  // Pause, resume and change need Edit; cancelling needs Cancel.
  const permissions = usePermissions();
  const canEdit = permissions.has('rp.recurring.edit');
  const canCancel = permissions.has('rp.recurring.cancel');
  const { data: customersData } = useRPCustomers();
  const customerLookup = useMemo(() => {
    const map = new Map<string, RPCustomer>();
    (customersData?.data ?? []).forEach((c: RPCustomer) => map.set(c.id, c));
    return map;
  }, [customersData]);

  const [editing, setEditing] = useState<RecurringSale | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<RecurringSale | null>(null);
  const [editingLink, setEditingLink] = useState<RPPaymentLink | null>(null);
  const [confirmCancelLink, setConfirmCancelLink] = useState<RPPaymentLink | null>(null);

  const rules = data?.data ?? [];

  // Merge recurring sales (auto-charge schedules) with recurring payment links
  // (links flagged is_recurring at creation). Both surface here as recurring items.
  const combinedRows = useMemo<RecurringRow[]>(() => {
    const saleRows: RecurringRow[] = rules.map((sale) => ({
      source: 'sale',
      id: sale.id,
      customerName:
        (sale.customerId ? customerLookup.get(sale.customerId)?.name : '') ||
        [sale.firstName, sale.lastName].filter(Boolean).join(' ') ||
        sale.cardHolderName ||
        '',
      sale,
    }));
    const linkRows: RecurringRow[] = (linksData?.data ?? [])
      .filter((link) => link.isRecurring)
      .filter((link) => !statusFilter || link.status === statusFilter)
      .map((link) => ({
        source: 'link',
        id: link.id,
        customerName: link.customer?.name || '',
        link,
      }));
    return [...saleRows, ...linkRows];
  }, [rules, linksData, customerLookup, statusFilter]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return combinedRows;
    return combinedRows.filter((row) => row.customerName.toLowerCase().includes(q));
  }, [combinedRows, search]);

  const loading = isLoading || linksLoading;
  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageRows = useMemo(
    () => filteredRows.slice((page - 1) * perPage, page * perPage),
    [filteredRows, page, perPage],
  );

  const togglePauseResume = async (rule: RecurringSale) => {
    const nextStatus = rule.status === 'active' ? 'paused' : 'active';
    try {
      await updateRule.mutateAsync({ id: rule.id, data: { status: nextStatus } });
      toast.success(`Recurring rule ${nextStatus === 'paused' ? 'paused' : 'resumed'}`);
    } catch (err: any) {
      toast.error(err?.error?.message || 'Failed to update recurring rule');
    }
  };

  const toggleLinkPauseResume = async (link: RPPaymentLink) => {
    const nextStatus = link.status === 'active' ? 'paused' : 'active';
    try {
      await updateLinkStatus.mutateAsync({ id: link.id, status: nextStatus });
      toast.success(`Recurring payment link ${nextStatus === 'paused' ? 'paused' : 'resumed'}`);
    } catch (err: any) {
      toast.error(err?.error?.message || 'Failed to update payment link');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold">Recurring / Scheduled</h3>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by customer name…"
              className="h-9 w-64 rounded-lg border border-input bg-white pl-9 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as typeof statusFilter);
              setPage(1);
            }}
            className="h-9 rounded-lg border border-input bg-white px-3 text-sm"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="scheduled">Scheduled</option>
            <option value="cancelled">Cancelled</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">ID</th>
              <th className="px-4 py-3 font-medium">Customer</th>
              <th className="px-4 py-3 font-medium">Amount</th>
              <th className="px-4 py-3 font-medium">Method</th>
              <th className="px-4 py-3 font-medium">Frequency</th>
              <th className="px-4 py-3 font-medium">Next Run</th>
              <th className="px-4 py-3 font-medium">End</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : total === 0 ? (
              <tr>
                <td colSpan={9}>
                  {search.trim() ? (
                    <EmptyState
                      icon={SearchX}
                      title="No matching recurring items"
                      description="Try a different search term."
                    />
                  ) : (
                    <EmptyState
                      icon={Repeat}
                      title="No recurring items yet"
                      description="Enable recurring on the New Sale tab or the Create Payment Link popup."
                    />
                  )}
                </td>
              </tr>
            ) : (
              pageRows.map((row) => {
                if (row.source === 'link') {
                  const link = row.link;
                  return (
                    <tr key={`link-${link.id}`} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-muted-foreground" title={link.id}>
                          {link.id.slice(0, 8)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{link.customer?.name || '—'}</div>
                        {link.customer?.company && (
                          <div className="text-xs text-muted-foreground">{link.customer.company}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium">{formatCurrency(link.amount)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Link2 className="h-4 w-4 text-muted-foreground" />
                          <span>Payment Link</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {link.recurringFrequency
                          ? (RECURRING_FREQUENCY_LABELS[link.recurringFrequency as RecurringFrequency] ??
                            link.recurringFrequency)
                          : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {link.recurringStartAt ? formatDateTime(link.recurringStartAt) : '—'}
                      </td>
                      <td className="px-4 py-3">{link.recurringEndAt ? formatDateTime(link.recurringEndAt) : '—'}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={link.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {(link.status === 'active' || link.status === 'paused') && (canEdit || canCancel) && (
                            <>
                              {canEdit && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => toggleLinkPauseResume(link)}
                                    disabled={updateLinkStatus.isPending}
                                    className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs font-medium hover:bg-muted disabled:opacity-50"
                                    title={link.status === 'active' ? 'Pause' : 'Resume'}
                                  >
                                    {link.status === 'active' ? (
                                      <>
                                        <Pause className="h-3.5 w-3.5" /> Pause
                                      </>
                                    ) : (
                                      <>
                                        <Play className="h-3.5 w-3.5" /> Resume
                                      </>
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setEditingLink(link)}
                                    className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs font-medium hover:bg-muted"
                                    title="Edit schedule"
                                  >
                                    <Pencil className="h-3.5 w-3.5" /> Edit
                                  </button>
                                </>
                              )}
                              {canCancel && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => setConfirmCancelLink(link)}
                                    className="inline-flex h-8 items-center gap-1 rounded-md border border-red-200 px-2 text-xs font-medium text-red-600 hover:bg-red-50"
                                    title="Cancel recurring link"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                }
                const rule = row.sale;
                const customer = rule.customerId ? customerLookup.get(rule.customerId) : null;
                const isCancelled = rule.status === 'cancelled';
                return (
                  <tr key={rule.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-muted-foreground" title={rule.id}>
                        {rule.id.slice(0, 8)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{customer?.name || '—'}</div>
                      {customer?.company && <div className="text-xs text-muted-foreground">{customer.company}</div>}
                    </td>
                    <td className="px-4 py-3 font-medium">{formatCurrency(rule.amount)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {rule.paymentMethod === 'card' ? (
                          <CreditCard className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Landmark className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="capitalize">
                          {rule.paymentMethod === 'card'
                            ? rule.cardLastFour
                              ? `Card •••• ${rule.cardLastFour}`
                              : 'Card'
                            : 'ACH'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">{RECURRING_FREQUENCY_LABELS[rule.frequency] ?? rule.frequency}</td>
                    <td className="px-4 py-3">{formatDateTime(rule.nextRunAt)}</td>
                    <td className="px-4 py-3">{rule.endAt ? formatDateTime(rule.endAt) : '—'}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={rule.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {!isCancelled && (canEdit || canCancel) && (
                          <>
                            {canEdit && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => togglePauseResume(rule)}
                                  disabled={updateRule.isPending}
                                  className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs font-medium hover:bg-muted disabled:opacity-50"
                                  title={rule.status === 'active' ? 'Pause' : 'Resume'}
                                >
                                  {rule.status === 'active' ? (
                                    <>
                                      <Pause className="h-3.5 w-3.5" /> Pause
                                    </>
                                  ) : (
                                    <>
                                      <Play className="h-3.5 w-3.5" /> Resume
                                    </>
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditing(rule)}
                                  className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs font-medium hover:bg-muted"
                                  title="Edit schedule"
                                >
                                  <Pencil className="h-3.5 w-3.5" /> Edit
                                </button>
                              </>
                            )}
                            {canCancel && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setConfirmCancel(rule)}
                                  className="inline-flex h-8 items-center gap-1 rounded-md border border-red-200 px-2 text-xs font-medium text-red-600 hover:bg-red-50"
                                  title="Cancel rule"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!loading && total > 0 && (
        <DataTablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          onPageChange={setPage}
          perPageOptions={PER_PAGE_OPTIONS}
          onPerPageChange={(n) => {
            setPerPage(n);
            setPage(1);
          }}
        />
      )}

      {editing && (
        <EditScheduleModal
          rule={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.success('Recurring rule updated');
          }}
        />
      )}

      <ConfirmDialog
        open={!!confirmCancel}
        title="Cancel recurring rule?"
        message={`This will stop future charges for ${
          (confirmCancel?.customerId && customerLookup.get(confirmCancel.customerId)?.name) || 'this rule'
        }. Cancelled rules cannot be reactivated.`}
        confirmLabel="Cancel rule"
        cancelLabel="Keep rule"
        variant="danger"
        loading={cancelRule.isPending}
        onConfirm={async () => {
          if (!confirmCancel) return;
          try {
            await cancelRule.mutateAsync(confirmCancel.id);
            toast.success('Recurring rule cancelled');
          } catch (err: any) {
            toast.error(err?.error?.message || 'Failed to cancel recurring rule');
          } finally {
            setConfirmCancel(null);
          }
        }}
        onCancel={() => setConfirmCancel(null)}
      />

      {editingLink && (
        <EditLinkScheduleModal
          link={editingLink}
          onClose={() => setEditingLink(null)}
          onSaved={() => {
            setEditingLink(null);
            toast.success('Recurring payment link updated');
          }}
        />
      )}

      <ConfirmDialog
        open={!!confirmCancelLink}
        title="Cancel recurring link?"
        message={`This will cancel the recurring payment link for ${
          confirmCancelLink?.customer?.name || 'this customer'
        }. Cancelled links cannot be reactivated.`}
        confirmLabel="Cancel link"
        cancelLabel="Keep link"
        variant="danger"
        loading={updateLinkStatus.isPending}
        onConfirm={async () => {
          if (!confirmCancelLink) return;
          try {
            await updateLinkStatus.mutateAsync({ id: confirmCancelLink.id, status: 'cancelled' });
            toast.success('Recurring payment link cancelled');
          } catch (err: any) {
            toast.error(err?.error?.message || 'Failed to cancel payment link');
          } finally {
            setConfirmCancelLink(null);
          }
        }}
        onCancel={() => setConfirmCancelLink(null)}
      />
    </div>
  );
}

// ── Edit Schedule Modal ──

function toDateInput(iso: string): string {
  return new Date(iso).toISOString().split('T')[0];
}

function toTimeInput(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function EditScheduleModal({
  rule,
  onClose,
  onSaved,
}: {
  rule: RecurringSale;
  onClose: () => void;
  onSaved: () => void;
}) {
  const updateRule = useUpdateRecurringSale();
  const [frequency, setFrequency] = useState<RecurringFrequency>(
    RECURRING_FREQUENCIES.includes(rule.frequency as RecurringFrequency)
      ? (rule.frequency as RecurringFrequency)
      : 'monthly',
  );
  const [nextRunDate, setNextRunDate] = useState(toDateInput(rule.nextRunAt));
  const [nextRunTime, setNextRunTime] = useState(toTimeInput(rule.nextRunAt));
  const [hasEnd, setHasEnd] = useState(!!rule.endAt);
  const [endDate, setEndDate] = useState(rule.endAt ? toDateInput(rule.endAt) : '');
  const [error, setError] = useState('');

  const submit = async () => {
    if (!nextRunDate) {
      setError('Next run date is required');
      return;
    }
    const nextRunIso = new Date(`${nextRunDate}T${nextRunTime || '00:00'}:00`).toISOString();
    let endAtIso: string | null = null;
    if (hasEnd) {
      if (!endDate) {
        setError('End date is required');
        return;
      }
      const endParsed = new Date(`${endDate}T23:59:59`);
      if (endParsed <= new Date(nextRunIso)) {
        setError('End date must be after the next run');
        return;
      }
      endAtIso = endParsed.toISOString();
    }
    setError('');
    try {
      await updateRule.mutateAsync({
        id: rule.id,
        data: {
          frequency,
          nextRunAt: nextRunIso,
          startAt: nextRunIso,
          endAt: endAtIso,
        },
      });
      onSaved();
    } catch (err: any) {
      setError(err?.error?.message || 'Failed to update recurring rule');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h3 className="text-sm font-semibold">Edit recurring schedule</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 px-5 py-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Frequency</label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as RecurringFrequency)}
              className={inputCls}
            >
              {RECURRING_FREQUENCIES.map((freq) => (
                <option key={freq} value={freq}>
                  {RECURRING_FREQUENCY_LABELS[freq]}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">Next run date</label>
              <DateInput value={nextRunDate} onChange={(e) => setNextRunDate(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Next run time</label>
              <input
                type="time"
                value={nextRunTime}
                onChange={(e) => setNextRunTime(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">End</label>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                <input
                  type="radio"
                  name="editEnd"
                  checked={!hasEnd}
                  onChange={() => setHasEnd(false)}
                  className="h-4 w-4 accent-primary"
                />
                <span className="text-sm">No end date</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                <input
                  type="radio"
                  name="editEnd"
                  checked={hasEnd}
                  onChange={() => setHasEnd(true)}
                  className="h-4 w-4 accent-primary"
                />
                <span className="text-sm">End on</span>
              </label>
            </div>
            {hasEnd && (
              <DateInput
                value={endDate}
                min={nextRunDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={cn(inputCls, 'mt-2')}
              />
            )}
          </div>

          {error && (
            <p className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700" role="alert">
              {error}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={updateRule.isPending}
            className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {updateRule.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Link Schedule Modal ──

function EditLinkScheduleModal({
  link,
  onClose,
  onSaved,
}: {
  link: RPPaymentLink;
  onClose: () => void;
  onSaved: () => void;
}) {
  const updateLink = useUpdateRPPaymentLink();
  const [frequency, setFrequency] = useState<RecurringFrequency>(
    (link.recurringFrequency as RecurringFrequency) || 'monthly',
  );
  const [startDate, setStartDate] = useState(link.recurringStartAt ? toDateInput(link.recurringStartAt) : '');
  const [startTime, setStartTime] = useState(link.recurringStartAt ? toTimeInput(link.recurringStartAt) : '09:00');
  const [hasEnd, setHasEnd] = useState(!!link.recurringEndAt);
  const [endDate, setEndDate] = useState(link.recurringEndAt ? toDateInput(link.recurringEndAt) : '');
  const [error, setError] = useState('');

  const submit = async () => {
    if (!startDate) {
      setError('Start date is required');
      return;
    }
    const startIso = new Date(`${startDate}T${startTime || '00:00'}:00`).toISOString();
    let endIso: string | null = null;
    if (hasEnd) {
      if (!endDate) {
        setError('End date is required');
        return;
      }
      const endParsed = new Date(`${endDate}T23:59:59`);
      if (endParsed <= new Date(startIso)) {
        setError('End date must be after the start date');
        return;
      }
      endIso = endParsed.toISOString();
    }
    setError('');
    try {
      await updateLink.mutateAsync({
        id: link.id,
        recurringFrequency: frequency,
        recurringStartAt: startIso,
        recurringEndAt: endIso,
      });
      onSaved();
    } catch (err: any) {
      setError(err?.error?.message || 'Failed to update payment link');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h3 className="text-sm font-semibold">Edit recurring payment link</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 px-5 py-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Frequency</label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as RecurringFrequency)}
              className={inputCls}
            >
              {RECURRING_FREQUENCIES.map((freq) => (
                <option key={freq} value={freq}>
                  {RECURRING_FREQUENCY_LABELS[freq]}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">Start date</label>
              <DateInput value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Start time</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">End</label>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                <input
                  type="radio"
                  name="editLinkEnd"
                  checked={!hasEnd}
                  onChange={() => setHasEnd(false)}
                  className="h-4 w-4 accent-primary"
                />
                <span className="text-sm">No end date</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                <input
                  type="radio"
                  name="editLinkEnd"
                  checked={hasEnd}
                  onChange={() => setHasEnd(true)}
                  className="h-4 w-4 accent-primary"
                />
                <span className="text-sm">End on</span>
              </label>
            </div>
            {hasEnd && (
              <DateInput
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={cn(inputCls, 'mt-2')}
              />
            )}
          </div>

          {error && (
            <p className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700" role="alert">
              {error}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={updateLink.isPending}
            className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {updateLink.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
